// Composer state with local (localStorage) + server (PATCH /api/drafts/:id with version) autosave.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Draft } from '@mailpilot/shared';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/keys';
import { parseAddressList } from '../lib/format';

export interface ComposeFields {
  mailboxId: string;
  to: string;
  cc: string;
  subject: string;
  body: string;
  signatureId: string | null;
}

interface Stored {
  fields: ComposeFields;
  draftId: string | null;
  version: number | null;
  savedAt: number;
}

export type SaveState = 'idle' | 'local' | 'saving' | 'saved' | 'error' | 'conflict';

const EDITABLE: Draft['status'][] = ['draft', 'pending_attachment'];
export const isEditableDraft = (d: Draft) => EDITABLE.includes(d.status) && (d.kind === 'suggestion' || d.kind === 'user');

function readStored(key: string): Stored | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}
function writeStored(key: string, v: Stored | null) {
  try {
    if (v) localStorage.setItem(key, JSON.stringify(v));
    else localStorage.removeItem(key);
  } catch {
    /* quota / private mode */
  }
}

export function fieldsFromDraft(d: Draft): ComposeFields {
  const fmt = (l: Draft['to']) => l.map((a) => (a.name ? `${a.name} <${a.email}>` : a.email)).join(', ');
  return { mailboxId: d.mailboxId, to: fmt(d.to), cc: fmt(d.cc), subject: d.subject, body: d.bodyText, signatureId: d.signatureId };
}

export function useDraftEditor(opts: {
  storageKey: string;
  threadId: string | null;
  inReplyToMessageId: string | null;
  initialDraft: Draft | null;
  defaults: ComposeFields;
  online: boolean;
}) {
  const { storageKey, threadId, inReplyToMessageId, online } = opts;
  const qc = useQueryClient();
  const init = useRef<{ fields: ComposeFields; draft: Draft | null; version: number | null; draftId: string | null } | null>(null);
  if (!init.current) {
    const stored = readStored(storageKey);
    const d = opts.initialDraft;
    if (stored && (!d || stored.draftId === d.id || !stored.draftId) && (!d || stored.savedAt > new Date(d.updatedAt).getTime())) {
      init.current = { fields: stored.fields, draft: d, draftId: stored.draftId ?? d?.id ?? null, version: stored.version ?? d?.version ?? null };
    } else if (d) {
      init.current = { fields: fieldsFromDraft(d), draft: d, draftId: d.id, version: d.version };
    } else {
      init.current = { fields: opts.defaults, draft: null, draftId: null, version: null };
    }
  }
  const [fields, setFieldsState] = useState<ComposeFields>(init.current.fields);
  const [draft, setDraft] = useState<Draft | null>(init.current.draft);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<unknown>(null);
  const idRef = useRef<string | null>(init.current.draftId);
  const versionRef = useRef<number | null>(init.current.version);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const dirtyRef = useRef(false);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const serverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistLocal = useCallback(
    (f: ComposeFields) => writeStored(storageKey, { fields: f, draftId: idRef.current, version: versionRef.current, savedAt: Date.now() }),
    [storageKey],
  );

  const adopt = useCallback(
    (d: Draft, replaceFields: boolean) => {
      idRef.current = d.id;
      versionRef.current = d.version;
      setDraft(d);
      if (replaceFields) {
        const f = fieldsFromDraft(d);
        // keep the user's chosen signature if the server draft has none
        if (!f.signatureId) f.signatureId = fieldsRef.current.signatureId;
        fieldsRef.current = f;
        setFieldsState(f);
        dirtyRef.current = false;
      }
      persistLocal(fieldsRef.current);
      if (d.threadId) void qc.invalidateQueries({ queryKey: qk.thread(d.threadId) });
    },
    [persistLocal, qc],
  );

  /** Save to the server now (serialised). Resolves with the saved draft (or null if nothing to save). */
  const flush = useCallback((): Promise<Draft | null> => {
    if (serverTimer.current) {
      clearTimeout(serverTimer.current);
      serverTimer.current = null;
    }
    const run = async (): Promise<Draft | null> => {
      const f = fieldsRef.current;
      if (!dirtyRef.current && idRef.current) return draftRef.current;
      if (!idRef.current && !f.body.trim() && !f.subject.trim()) return null;
      setSaveState('saving');
      const body = {
        mailboxId: f.mailboxId,
        to: parseAddressList(f.to),
        cc: parseAddressList(f.cc),
        subject: f.subject,
        bodyText: f.body,
        signatureId: f.signatureId,
      };
      try {
        let res: { draft: Draft };
        if (idRef.current && versionRef.current !== null) {
          res = await api('PATCH /api/drafts/:id', { params: { id: idRef.current }, body: { ...body, version: versionRef.current } });
        } else {
          res = await api('POST /api/drafts', { body: { ...body, threadId, inReplyToMessageId, kind: 'user' } });
        }
        dirtyRef.current = false;
        idRef.current = res.draft.id;
        versionRef.current = res.draft.version;
        setDraft(res.draft);
        persistLocal(fieldsRef.current);
        setSaveState('saved');
        setSaveError(null);
        return res.draft;
      } catch (e) {
        const conflict = e instanceof ApiError && (e.status === 409 || /version|conflict/i.test(e.code));
        setSaveState(conflict ? 'conflict' : e instanceof ApiError && e.isNetwork ? 'local' : 'error');
        setSaveError(e);
        throw e;
      }
    };
    const p = chain.current.then(run, run);
    chain.current = p.catch(() => undefined);
    return p;
  }, [threadId, inReplyToMessageId, persistLocal]);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const setFields = useCallback(
    (patch: Partial<ComposeFields>) => {
      const next = { ...fieldsRef.current, ...patch };
      fieldsRef.current = next;
      setFieldsState(next);
      dirtyRef.current = true;
      persistLocal(next);
      setSaveState('local');
      if (serverTimer.current) clearTimeout(serverTimer.current);
      serverTimer.current = setTimeout(() => {
        if (navigator.onLine) void flush().catch(() => undefined);
      }, 1500);
    },
    [flush, persistLocal],
  );

  // when we come back online, push local edits
  useEffect(() => {
    if (online && dirtyRef.current) void flush().catch(() => undefined);
  }, [online, flush]);

  useEffect(
    () => () => {
      if (serverTimer.current) clearTimeout(serverTimer.current);
    },
    [],
  );

  /** Reload from the server copy after a version conflict. */
  const reloadFrom = useCallback((d: Draft) => adopt(d, true), [adopt]);

  const reset = useCallback(
    (defaults: ComposeFields) => {
      writeStored(storageKey, null);
      idRef.current = null;
      versionRef.current = null;
      dirtyRef.current = false;
      fieldsRef.current = defaults;
      setFieldsState(defaults);
      setDraft(null);
      setSaveState('idle');
    },
    [storageKey],
  );

  return { fields, setFields, draft, adopt, flush, reset, reloadFrom, saveState, saveError };
}
